import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { usePackages, Package } from '../../crm-packages/useData';

export interface OpenPackagesOverviewProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
}

interface PackageTaskLink {
  id: number;
  taskId?: number | null;
  task?: {
    taskId: number;
    taskAssignedTo?: number | null;
  };
}

const OpenPackagesOverview: React.FC<OpenPackagesOverviewProps> = ({
  limit: limitProp = 5,
  embedded,
  settings,
}) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { data: packages = [], isLoading } = usePackages();
  const limit = typeof settings?.limit === 'number' ? settings.limit : limitProp;
  const cardClass = embedded
    ? 'min-w-0 flex-1 flex flex-col min-h-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;
  const listClass = embedded ? 'flex-1 min-h-0 overflow-y-auto space-y-1' : 'space-y-1';

  const openPackages = (Array.isArray(packages) ? packages : []).filter(
    (pkg: Package) => pkg.isOpen === true
  );

  const perPackage = openPackages.map((pkg: Package) => {
    const links = (pkg.crm_packages_tasks ?? []).filter(
      (link: PackageTaskLink) => Boolean(link.task)
    );
    const unassignedTasks = links.filter(
      (link: PackageTaskLink) => (link.task?.taskAssignedTo ?? null) === null
    ).length;
    const remainingMinutes =
      pkg.remainingMinutes ?? ((pkg.packageMinutes ?? 0) - (pkg.totalInvoicedMinutes ?? 0));

    return {
      id: pkg.id,
      title: pkg.packageDescr?.trim() || `Package #${pkg.id}`,
      clientName: pkg.client?.clientName?.trim() || 'No client',
      unassignedTasks,
      remainingMinutes,
    };
  });

  const totals = perPackage.reduce(
    (acc, pkg) => ({
      unassignedTasks: acc.unassignedTasks + pkg.unassignedTasks,
      remainingMinutes: acc.remainingMinutes + pkg.remainingMinutes,
    }),
    { unassignedTasks: 0, remainingMinutes: 0 }
  );

  const listItems = perPackage
    .sort((a, b) => {
      if (b.unassignedTasks !== a.unassignedTasks) return b.unassignedTasks - a.unassignedTasks;
      return a.remainingMinutes - b.remainingMinutes;
    })
    .slice(0, limit);

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-3`}>Open Packages Overview</h2>
        <div className="space-y-2 sm:space-y-3">
          {[1, 2, 3].map((item) => (
            <div key={item} className={`flex items-center space-x-3 sm:space-x-4 pb-2 sm:pb-3 ${colors.border} border-b last:border-0`}>
              <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded ${colors.surfaceHover} animate-pulse flex-shrink-0`}></div>
              <div className="flex-1 min-w-0">
                <div className={`h-3 sm:h-4 ${colors.surfaceHover} animate-pulse rounded w-3/4 mb-1 sm:mb-2`}></div>
                <div className={`h-2.5 sm:h-3 ${colors.surfaceHover} animate-pulse rounded w-1/2`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className={`text-base font-semibold ${colors.text}`}>Open Packages Overview</h2>
        <button
          onClick={() => navigate('/crm-packages')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium whitespace-nowrap transition-colors"
        >
          View All
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2">
          <p className={`text-[10px] uppercase tracking-wide ${colors.textMuted}`}>Unassigned</p>
          <p className={`text-sm font-semibold ${colors.text}`}>{totals.unassignedTasks}</p>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2">
          <p className={`text-[10px] uppercase tracking-wide ${colors.textMuted}`}>Remaining</p>
          <p className={`text-sm font-semibold ${colors.text}`}>{totals.remainingMinutes} min</p>
        </div>
      </div>

      <div className={listClass}>
        {listItems.length > 0 ? (
          listItems.map((pkg) => (
            <div
              key={pkg.id}
              onClick={() => navigate(`/crm-packages/${pkg.id}`)}
              className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
            >
              <div className="flex-1 min-w-0">
                <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-1 mb-1`}>{pkg.title}</p>
                <p className={`text-[10px] sm:text-xs ${colors.textSecondary} line-clamp-1 mb-2`}>{pkg.clientName}</p>
                <div className={`flex flex-wrap items-center gap-3 text-[10px] sm:text-xs ${colors.textMuted}`}>
                  <span>Unassigned: {pkg.unassignedTasks}</span>
                  <span>Remaining: {pkg.remainingMinutes} min</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={`text-center py-8 ${colors.textMuted}`}>
            <p className="text-sm sm:text-base">No open packages</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenPackagesOverview;
